import { transitionTreatmentSession } from "./authorityApi";
import { TREATMENT_RUNTIME_ACTION } from "./treatmentApiConstants";

/**
 * Re-price a prepared checkout with a different coupon code.
 *
 * The server rebuilds checkout-ready from the immutable release and issues a
 * FRESH checkout capability, which overwrites the previous one. That is what
 * makes applying a coupon at the payment step safe: the patient can never
 * authorize against a capability that is bound to a different total, because
 * the old capability stops being valid the moment the quote changes.
 *
 * Selections are replayed from the prepared checkout rather than rebuilt from
 * the browser, so a re-quote cannot silently change what was selected. The
 * server still re-resolves every option, price, and total from the release.
 */

export const selectionsFromCheckout = (checkout) => (
  (checkout?.treatments || []).flatMap((treatment) => (
    (treatment.products || []).map((product) => {
      const optionId = String(product.option_id || "");
      if (!optionId) return null;
      const selection = { option_id: optionId };
      const quantity = Number(product.quantity);
      if (Number.isInteger(quantity) && quantity > 0) {
        selection.quantity = quantity;
      }
      const days = Number(product.days_supply);
      if (Number.isInteger(days) && days > 0) {
        selection.days_supply = days;
      }
      return selection;
    })
  )).filter(Boolean)
);

export const labSelectionsFromCheckout = (checkout) => [
  ...new Set(
    (checkout?.treatments || []).flatMap((treatment) => (
      (treatment.labs || []).map((lab) => String(lab.assignment_id || ""))
    )).filter(Boolean),
  ),
];

export const requoteTreatmentCheckout = async (
  checkout,
  couponCode,
  editedSelections = null,
) => {
  const selections = Array.isArray(editedSelections)
    ? editedSelections
      .map((selection) => {
        const optionId = String(selection?.option_id || "");
        if (!optionId) return null;
        const normalized = { option_id: optionId };
        const quantity = Number(selection?.quantity);
        const days = Number(selection?.days_supply);
        if (Number.isInteger(quantity) && quantity > 0) normalized.quantity = quantity;
        if (Number.isInteger(days) && days > 0) normalized.days_supply = days;
        return normalized;
      })
      .filter(Boolean)
    : selectionsFromCheckout(checkout);
  if (!selections.length) {
    throw new Error("This checkout has no treatment selections to re-price.");
  }
  const next = await transitionTreatmentSession(
    checkout,
    TREATMENT_RUNTIME_ACTION.selectProducts,
    {
      selections,
      lab_selections: labSelectionsFromCheckout(checkout),
      coupon_code: String(couponCode || "").trim(),
    },
  );
  if (!next?.checkout_capability) {
    throw new Error("Checkout could not be re-priced. Please refresh and try again.");
  }
  return {
    ...next,
    patient_name: checkout.patient_name,
    // A changed total is a changed intent, so the replay key must change with
    // it. Reusing the old key would let a resubmit settle the previous amount.
    client_idempotency_key: (
      globalThis.crypto?.randomUUID?.()
      || `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`
    ),
  };
};
